import { useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  MarkAsPaidSaleInvoiceDocument,
  SaleInvoicesDetailsPageRouteDocument,
  SaleInvoicesListTableDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export const useMarkAsPaidSaleInvoiceDialog = () => {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean
    invoiceId?: string
    companyId?: string
    callback?: () => void
  }>({ isOpen: false })

  const [markAsPaid] = useMutation(MarkAsPaidSaleInvoiceDocument, {
    extractorFn: (data) => data.financeMutations.markAsPaidSaleFinanceFinancialDocument,
    refetchQueries: [SaleInvoicesListTableDocument, SaleInvoicesDetailsPageRouteDocument],
  })

  const showMarkAsPaidSaleInvoiceDialog = ({
    invoiceId,
    companyId,
    callback,
  }: {
    invoiceId: string
    companyId: string
    callback?: () => void
  }) => {
    setDialogState({ isOpen: true, invoiceId, companyId, callback })
  }

  const handleClose = () => {
    setDialogState({ isOpen: false })
  }

  const handleConfirm = async () => {
    if (!dialogState.invoiceId || !dialogState.companyId) return

    const result = await markAsPaid({
      variables: {
        companyId: dialogState.companyId,
        ids: [dialogState.invoiceId],
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.sale_invoices.mark_as_paid.success'),
      })
      if (dialogState.callback) dialogState.callback()
    }

    track(trackingEvents.accounting.saleInvoices.markAsPaid)
    handleClose()
  }

  const MarkAsPaidDialog = dialogState.isOpen ? (
    <F0Dialog
      isOpen
      onClose={handleClose}
      title={i18n.t('finance.sale_invoices.mark_as_paid.title')}
      description={i18n.t('finance.sale_invoices.mark_as_paid.content')}
      primaryAction={{
        label: i18n.t('finance.sale_invoices.mark_as_paid.title'),
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    />
  ) : null

  return { showMarkAsPaidSaleInvoiceDialog, MarkAsPaidDialog, handleConfirm }
}
